import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import usePropertyMacros from "../../../utils/usePropertyMacros";

const toStr = (v) => (v == null ? "" : String(v));

const PropertyDetailsModal = ({ open, rentalId, onClose }) => {
  const { rows, loadingListingText } = usePropertyMacros(rentalId);
  const modalRef = useRef(null);
  const searchRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);
  const [q, setQ] = useState("");
  const [visible, setVisible] = useState(false);

  // Animate in on open, reset search
  useEffect(() => {
    if (open) {
      setQ("");
      // trigger CSS transition on next frame
      requestAnimationFrame(() => setVisible(true));
      setTimeout(() => searchRef.current?.focus(), 150);
    } else {
      setVisible(false);
    }
  }, [open]);

  // Stop UIkit offcanvas focus-trap from stealing focus away from this modal
  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    el.addEventListener("focusin", stop, true);
    el.addEventListener("focusout", stop, true);
    el.addEventListener("focus", stop, true);
    return () => {
      el.removeEventListener("focusin", stop, true);
      el.removeEventListener("focusout", stop, true);
      el.removeEventListener("focus", stop, true);
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const visibleRows = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) => {
      const label = (r.label || "").toLowerCase();
      const raw = (r.rawLabel || "").toLowerCase();
      const val = (r.value || "").toLowerCase();
      return label.includes(query) || raw.includes(query) || val.includes(query);
    });
  }, [rows, q]);

  const markCopied = (rowId) => {
    setCopiedId(rowId);
    window.setTimeout(() => setCopiedId(null), 1200);
  };

  const copyToClipboard = async (text, rowId) => {
    const value = toStr(text);
    if (!value.trim()) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        markCopied(rowId);
        return;
      }
    } catch {}
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) markCopied(rowId);
    } catch {}
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={modalRef}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: visible ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
        overflow: "auto",
        transition: "background 0.2s ease",
      }}
      onClick={(e) => {
        if (e.target === modalRef.current) onClose?.();
      }}
    >
      <div
        style={{
          width: 720,
          maxWidth: "95vw",
          height: 480,
          minHeight: 480,
          background: "#fff",
          borderRadius: 4,
          padding: 30,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-30px)",
          transition: "opacity 0.2s ease, transform 0.2s ease",
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 20,
            lineHeight: 1,
            padding: 4,
            color: "#666",
          }}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 style={{ margin: "0 0 16px 0", fontSize: 20, fontWeight: 600 }}>
          Property Details
        </h2>

        <div style={{ marginBottom: 16 }}>
          <input
            ref={searchRef}
            className="uk-input"
            placeholder="Search..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {loadingListingText && (
          <div style={{ fontSize: 13, color: "#999", marginBottom: 8 }}>
            Loading extra fields…
          </div>
        )}

        <div>
          {visibleRows.length === 0 ? (
            <div style={{ color: "#999" }}>No property details found.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {visibleRows.map((r) => (
                <div
                  key={r.id}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 4,
                    padding: "10px 14px",
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 600, textTransform: "none" }}>
                      {r.label}
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(r.value, r.id)}
                      aria-label="Copy"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={copiedId === r.id ? "/images/copied.svg" : "/images/copy.svg"}
                        alt={copiedId === r.id ? "Copied" : "Copy"}
                        style={{ width: 18, height: 18, opacity: 0.8 }}
                      />
                    </button>
                  </div>

                  <div style={{ height: 1, background: "#e5e5e5", margin: "8px 0 10px 0" }} />

                  <div style={{ wordBreak: "break-word" }}>{r.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PropertyDetailsModal;
